/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable max-len */
/* eslint-disable max-classes-per-file */
import React from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import DeleteIcon from '../Icons/DeleteIcon';
import Validation from '../Validation';
import { getProp, setProp } from '../Utils/formHelpers';
import Highlighter from '../Highlighter';
import SearchIcon from '../Icons/SearchIcon';
import Helpers from '../Utils/nodeHelpers';
import Spinner from '../Spinner';
import { getTitle, getRandomId, Presentation } from '../Utils/baseComponentHelper';
import { FormWrapperPropTypes } from '../Form/withForm';
import Log from '../../utils/log';
import SearchListProvider from './SearchListProvider';
import useActivation from '../Hooks/useActivation';
import EllipsisText from '../EllipsisText';
import useValidations from '../Hooks/useValidations';
import ValidationViewer from '../Validation/ValidationViewer';
import useMouseOver from '../Hooks/useMouseOver';

/**
 * Bottone di ricerca.
 * E' necessario capire quando il mouse e dentro o fuori dal bottone di recerca per
 * attivare/disattivare il blur.
 * Se non lo faccio e il dato NON è dirty, scatterebbe la conferma del valore anche se sto cercando di aprire la lista.
 * Questo va impedito.
 */
const SearchButton = (props) => {
  const { onEnter, onExit, onClick } = props;

  const [, onMouseOver, onMouseOut] = useMouseOver(onEnter, onExit);
  return (
    <SearchIcon
      onMouseOver={onMouseOver}
      onFocus={onMouseOver}
      onMouseOut={onMouseOut}
      onBlur={onMouseOut}
      className="autocomplete__icon"
      onClick={onClick}
    />
  );
};

SearchButton.propTypes = {
  onEnter: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

class AutoComplete extends React.Component {
  constructor(props) {
    super(props);
    const {
      renderProperty, stato, name, value,
    } = props;
    this.state = {
      formDirty: false,
      dirty: false,
      renderProperty: renderProperty || 'Description',
      value: (stato && name && getProp(name, stato)) || value || '',
      searchResult: [],
      isSearchResultListVisible: false,
      searchResultListSelectedItem: -1,
      loading: false,
    };
    this.deactiveIgnoreBlur();

    this.searchListProvider = new SearchListProvider({
      searchStarted: () => {
        this.setState({ loading: true });
      },

      searchCompleted: () => {
        this.setState({ loading: false });
      },

      errorCatched: (error) => {
        const { onGetError } = this.props;
        onGetError && onGetError(error);
      },

      cancel: () => {
        this.setState({
          searchResult: [],
          isSearchResultListVisible: false,
          searchResultListSelectedItem: -1,
          loading: false,
        });
      },

      loaded: (response, closeList) => {
        this.setState({
          searchResult: closeList ? [] : response,
          isSearchResultListVisible: !closeList,
          searchResultListSelectedItem: 0,
        }, this.selectFirstItemIfNoEditing);
      },
    });

    this.uniqueid = getRandomId();
  }

  componentDidMount() {
    const { name, addToForm } = this.props;

    if (addToForm && name) {
      addToForm(name, this.valida(), this.reset);
    }
  }

  UNSAFE_componentWillReceiveProps({
    value, name, renderProperty, stato, validare,
  }) {
    const { value: valueS } = this.state;
    const { validare: validareP, renderProperty: renderPropertyP } = this.props;

    if (value !== undefined && value !== null && valueS !== value) {
      this.setState({
        value,
        dirty: false,
      });
    }

    if (name != null && valueS !== getProp(name, stato)) {
      this.setState({
        value: getProp(name, stato),
      });
    }

    if (validareP !== validare && validare === false) {
      this.setState({
        formDirty: false,
      });
    }

    if (renderPropertyP !== renderProperty) {
      this.setState({ renderProperty });
    }
  }

  componentWillUnmount() {
    const { name, removeFromForm } = this.props;

    if (removeFromForm && name) {
      removeFromForm(name);
    }
  }

  /**
   * If the list is loaded when I finished editing
   * or not when someone has forced the list to reload,
   * I set the value.
   */
  selectFirstItemIfNoEditing = () => {
    const { suggest } = this.props;
    const { dirty, forcesSearch, searchResult } = this.state;
    if (!dirty && !suggest && !forcesSearch) {
      if (searchResult.length === 0) {
        this.clear();
      } else {
        this.selectItem(0);
      }
    }
  };

  getTitle = () => {
    const { title, validations } = this.props;
    return getTitle(title, validations);
  };

  /**
   * @public
   */
  // eslint-disable-next-line react/no-unused-class-component-methods
  isValid = () => this.valida().valid;

  reset = () => {
    this.setState({ formDirty: false, dirty: false });
  };

  search = (value) => {
    const { url, dataSource } = this.props;
    const { renderProperty } = this.state;
    this.searchListProvider.search({ url, dataSource, renderProperty }, value);
  };

  handleOnChange = (event) => {
    this.deactiveIgnoreBlur();
    const {
      minLength, readOnly, disabled, upperCase,
    } = this.props;
    if (readOnly || disabled) return;
    let eventValue = event.target.value;
    if (upperCase) eventValue = eventValue.toUpperCase();
    const cValue = new Presentation({ readOnly }).parse(eventValue);
    this.setState({ value: cValue, dirty: true, formDirty: true }, () => {
      if (cValue.length >= minLength) {
        this.search(cValue);
      } else {
        this.searchListProvider.cancel();
      }
    });
  };

  forceSearch = () => {
    const {
      disabled, url, dataSource, renderProperty,
    } = this.props;
    const { value } = this.state;
    if (disabled) return;
    const { isSearchResultListVisible } = this.state;
    if (isSearchResultListVisible) {
      this.searchListProvider.cancel();
    } else {
      this.setState({ forcesSearch: true }, () => {
        // finally not supported in building!
        const onFinally = () => this.setState({ forcesSearch: false });
        this.searchListProvider.searchNow({ url, dataSource, renderProperty }, value)
          .then(
            (res) => Promise.resolve(onFinally()).then(() => res),
            (err) => Promise.resolve(onFinally()).then(() => { throw err; }),
          );
        this.refInput.focus();
      });
    }
  };

  handleKeyUp = (event) => {
    const {
      searchResult, searchResultListSelectedItem, isSearchResultListVisible,
      renderProperty,
    } = this.state;
    const { readOnly, disabled } = this.props;

    if (readOnly || disabled) return;
    const searchTerm = searchResult[searchResultListSelectedItem];
    switch (event.keyCode) {
      //  Arrow up
      case 38:
        if (isSearchResultListVisible === true && searchResultListSelectedItem > 0) {
          this.setState({
            searchResultListSelectedItem: searchResultListSelectedItem - 1,
          });
        }
        break;
      //  Arrow down
      case 40:
        if (isSearchResultListVisible === true
          && searchResultListSelectedItem < searchResult.length - 1) {
          this.setState({
            searchResultListSelectedItem: searchResultListSelectedItem + 1,
          });
        } else if (!isSearchResultListVisible) {
          this.search('');
        }
        break;
      //  Tab
      case 9:
      //  Enter
      case 13: // eslint-disable-line no-fallthrough
        this.selectItemAfterLoading();
        Helpers.focusNextElement(event.target);
        break;
      //  Arrow left
      case 37:
      //  Arrow right
      // eslint-disable-next-line no-fallthrough
      case 39: {
        if (searchTerm) {
          this.setState({
            value: searchTerm[renderProperty],
          });
          this.search(searchTerm);
        }
        break;
      }
      default:
        break;
    }
  };

  valida = () => {
    const {
      name, validations, value, stato,
    } = this.props;
    if (validations === undefined) { return { valid: true }; }
    return Validation.validateAll(validations, (stato && name && getProp(name, stato)) || value || '');
  };

  activeIgnoreBlur = () => {
    this.ignoreBlur = true;
  };

  deactiveIgnoreBlur = () => {
    this.ignoreBlur = false;
  };

  handleListItemClick = (elementIndex) => {
    this.activeIgnoreBlur();
    this.selectItem(elementIndex);
  };

  handleOnBlur = () => {
    const { dirty } = this.state;
    this.setState({ formDirty: true });
    if (this.ignoreBlur) {
      this.deactiveIgnoreBlur();
      return;
    }
    if (!dirty) {
      this.setState({ isSearchResultListVisible: false });
      return;
    }
    this.selectItemAfterLoading();
  };

  selectText = () => {
    const {
      name, clearAfterSelect, onChange, onFormChange, stato,
    } = this.props;
    const { value } = this.state;

    const searchTerm = value;
    this.setState({
      value: clearAfterSelect ? '' : searchTerm,
      isSearchResultListVisible: false,
      dirty: false,
    }, () => {
      if (onFormChange && name) {
        const obj = {};
        setProp(name, value, obj, stato);
        onFormChange(obj, name, value);
      }
      onChange && onChange(searchTerm);
    });
  };

  getSearchTermDescription = (searchTerm) => {
    const { renderProperty } = this.state;
    const searchTermValue = searchTerm[renderProperty];
    if (!searchTermValue) {
      // eslint-disable-next-line no-console
      Log.warn('AutoComplete: controllare la prop renderProperty; il valore selezionato ritorna una descrizione nulla');
    }
    return searchTermValue;
  };

  selectItem = (index) => {
    const {
      name, clearAfterSelect, onChange, onFormChange, stato,
    } = this.props;
    const { searchResult } = this.state;
    const searchTerm = searchResult[index];
    let data = {};
    let callback = () => { };
    if (onFormChange && name) {
      data = {
        value: clearAfterSelect ? '' : this.getSearchTermDescription(searchTerm),
        isSearchResultListVisible: false,
        dirty: false,
      };
      callback = () => {
        const obj = {};
        setProp(name, data.value, obj, stato);
        onFormChange(obj, name, data.value);
        onChange && onChange(searchTerm);
      };
    } else {
      data = {
        value: clearAfterSelect ? '' : this.getSearchTermDescription(searchTerm),
        isSearchResultListVisible: false,
        dirty: false,
      };
      callback = () => { onChange && onChange(searchTerm); };
    }
    this.setState(data, callback);
  };

  resolveListItemClass = (index) => {
    const { searchResultListSelectedItem } = this.state;
    return index === searchResultListSelectedItem ? ' autocomplete__item--active' : '';
  };

  onClearAutoComplete = () => {
    this.searchListProvider.cancel();
    this.clearAutoComplete();
  };

  clearAutoComplete = () => {
    const {
      disabled, onClear, name, onChange, onFormChange, stato,
    } = this.props;
    this.activeIgnoreBlur();
    if (disabled) return;
    onClear && onClear();
    if (onFormChange && name) {
      const obj = {};
      setProp(name, '', obj, stato);
      onFormChange(obj, name, '');
    }
    this.setState({
      value: '',
      isSearchResultListVisible: false,
      dirty: false,
    });
    onChange && onChange('');
  };

  clear = () => {
    this.setState({
      value: '',
      dirty: false,
    });
  };

  scrollOptionIntoView = () => {
    setTimeout(() => {
      if (this.selectedItem == null || this.refContainer == null) {
        return;
      }
      const selectedDOM = this.selectedItem;
      const menuDOM = this.refContainer;
      const selectedRect = selectedDOM.getBoundingClientRect();
      const menuRect = menuDOM.getBoundingClientRect();
      if (selectedRect.bottom > (menuRect.bottom - 5) || selectedRect.top < menuRect.top) {
        menuDOM.scrollTop = (selectedDOM.offsetTop
          + selectedDOM.clientHeight - menuDOM.offsetHeight);
      }
    });
  };

  renderList = () => {
    const {
      customList,
      renderRow,
    } = this.props;
    const {
      value,
      searchResult,
      searchResultListSelectedItem,
      isSearchResultListVisible,
      renderProperty,
    } = this.state;

    if (customList) {
      return customList(
        value,
        searchResult,
        searchResultListSelectedItem,
        this.handleListItemClick,
        isSearchResultListVisible,
      );
    }
    return (
      <ul
        className="autocomplete__container"
        ref={(r) => { this.refContainer = r; }}
      >
        {searchResult && isSearchResultListVisible && searchResult.length > 0
          && searchResult.map((element, index) => (
            <li
              className={`autocomplete__item${this.resolveListItemClass(index)}`}
              key={index} // eslint-disable-line react/no-array-index-key
              role="presentation"
              onClick={() => this.handleListItemClick(index)}
              ref={(r) => {
                if (index === searchResultListSelectedItem) {
                  this.selectedItem = r;
                }
              }}
            >
              {(renderRow && renderRow(searchResult[index], value, index)) || (
                <Highlighter
                  text={index === 0
                    ? this.getSearchTermDescription(searchResult[index])
                    : searchResult[index][renderProperty]}
                  value={value}
                />
              )}
            </li>
          ))}
        {searchResult && isSearchResultListVisible && searchResult.length === 0 && (
          <li
            className="autocomplete__item"
            role="presentation"
            onClick={this.clearAutoComplete}
          >
            <span>Nessun risultato per questa ricerca</span>
          </li>
        )}
      </ul>
    );
  };

  selectItemAfterLoading() {
    const { searchResultListSelectedItem, searchResult, value } = this.state;
    const {
      suggest, stato, name, value: valueP,
    } = this.props;
    if (searchResultListSelectedItem < searchResult.length && searchResultListSelectedItem >= 0) {
      this.selectItem(searchResultListSelectedItem);
    } else if (suggest) {
      this.selectText();
    } else {
      const val = (stato && name && getProp(name, stato)) || valueP || '';
      if (val !== value) {
        this.clearAutoComplete();
      }
    }
  }

  render() {
    const {
      actions,
      disabled: disabledProp,
      readOnly: readOnlyProp,
      isValid,
      left,
      minLength,
      name,
      title,
      validare,
      addToForm,
      onFormChange,
      removeFromForm,
      resetta,
      stato,
      clearAfterSelect,
      customList,
      onChange,
      onClear,
      renderProperty,
      renderRow,
      suggest,
      url,
      validations,
      value: valueS,
      dataSource,
      ellipsis,
      upperCase,
      className,
      placeholder,
      ...others
    } = this.props;
    const {
      formDirty,
      value,
      searchResult,
      loading,
      isSearchResultListVisible,
    } = this.state;
    const { disabled, readOnly } = useActivation(disabledProp, readOnlyProp);
    const validazione = this.valida();
    if (isValid != null && name) {
      isValid(name, validazione.valid, validazione.messages || []);
    }

    const { hasError, hasWarning } = useValidations(validare, formDirty, validazione);

    const classes = cns(
      'autocomplete',
      hasError ? 'autocomplete--invalid' : 'autocomplete--valid',
      {
        'autocomplete--disabled': disabled,
        'autocomplete--nonEmptySearch': minLength > 1,
        'autocomplete--left': left,
        'autocomplete--readOnly': readOnly,
      },
      value ? 'autocomplete--valorized' : 'autocomplete--empty',
      isSearchResultListVisible ? 'autocomplete--open' : 'autocomplete--closed',
      className,
    );
    const inputClasses = cns('autocomplete__input', 'validationViewer__target', { 'autocomplete__input--highlighted': isSearchResultListVisible && searchResult.length > 0 });
    if (searchResult && isSearchResultListVisible && searchResult.length > 0 && this.selectedItem) {
      this.scrollOptionIntoView();
    }
    const inputTitleClass = cns('autocomplete__title', { 'autocomplete__title--readOnly': readOnly });

    const label = name || title;
    return (
      <div className={classes}>
        <div className="autocomplete__maincontainer">
          {readOnly
            ? (
              <span
                className={inputClasses}
                readOnly={readOnly}
                disabled={disabled}
                {...others}
              >
                <EllipsisText {...ellipsis}>
                  {new Presentation({ readOnly }).format(value)}
                </EllipsisText>
              </span>
            )
            : (
              <input
                id={this.uniqueid}
                autoComplete={`autocomplete-${this.uniqueid}`}
                className={inputClasses}
                readOnly={readOnly}
                disabled={disabled}
                data-origin={name}
                onBlur={() => setTimeout(this.handleOnBlur, 250)} // I postpone the event to handled list item click before.
                onChange={this.handleOnChange}
                onKeyDown={this.handleKeyUp}
                ref={(r) => { this.refInput = r; }}
                type="text"
                value={new Presentation({ readOnly }).format(value)}
                placeholder={placeholder}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...others}
              />
            )}
          {loading && <Spinner small />}
          {!readOnly && value !== '' && (
            <span
              className="autocomplete__clear"
              role="presentation"
              onClick={this.onClearAutoComplete}
            >
              <DeleteIcon />
            </span>
          )}
          {!readOnly && (
            <SearchButton
              onEnter={this.activeIgnoreBlur}
              onExit={this.deactiveIgnoreBlur}
              onClick={this.forceSearch}
            />
          )}
          <span className="autocomplete__bar" />
          {label && <label htmlFor={this.uniqueid} className="input__label">{label}</label>}
          {title && <div className={inputTitleClass}>{this.getTitle()}</div>}
          {this.renderList()}
          <ValidationViewer hasError={hasError} hasWarning={hasWarning} validazione={validazione} />
          <div className="clearBoth" />
        </div>
        {!readOnly && actions && <div className="autocomplete__actions">{actions}</div>}
      </div>
    );
  }
}

AutoComplete.defaultProps = {
  actions: null,
  clearAfterSelect: false,
  customList: null,
  disabled: false,
  readOnly: false,
  left: false,
  minLength: 3,
  name: null,
  onChange: null,
  onClear: null,
  renderProperty: null,
  renderRow: null,
  suggest: false,
  title: null,
  url: null,
  validations: null,
  value: null,
  dataSource: null,
  ellipsis: {
    left: false,
    top: true,
    disabled: false,
  },
  upperCase: false,
  className: null,
  placeholder: undefined,
};

AutoComplete.propTypes = {
  ...FormWrapperPropTypes,

  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,

  /** Lista di icone da mostrare dopo l'autocomplete */
  actions: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /** Indica se pulire il campo di input dopo la selezione */
  clearAfterSelect: PropTypes.bool,
  /**
   * Sovrascrive il rendering dei risultati:
   * ```
   *  customList(value, searchResult, searchResultListSelectedItem,
        handleListItemClick, isSearchResultListVisible)
   * ```
   * dove:
   * * *value*: valore cercato
   * * *searchResult*: la lista dei risultati
   * * *searchResultListSelectedItem*: l'elemento correntemente selezionato
   * * *handleListItemClick*: la callback di selezione
   * * *isSearchResultListVisible*: *non commentata*
   */
  customList: PropTypes.func,
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Presenta il campo in modalità sola lettura */
  readOnly: PropTypes.bool,
  /** Indica se aprire la tendina a sinistra */
  left: PropTypes.bool,
  /** Il numero di caratteri minimo da inserire per scatenare le ricerche */
  minLength: PropTypes.number,
  /** Nome del campo da bindare se il componente è contenuto all'interno di un componente Form */
  name: PropTypes.string,
  /**
   * Callback invocata quando è stato selezionato un valore:
   * ```
   *  onChange(searchTerm)
   * ```
   * dove:
   * * *searchTerm*: l'elemento selezionato
   */
  onChange: PropTypes.func,
  /**
   * Callback invocata quando viene cliccato il pulsante per pulire l'autocomplete:
   * ```
   *  onClear()
   * ```
   */
  onClear: PropTypes.func,
  /** Proprietà da visualizzare nelle righe */
  renderProperty: PropTypes.string,
  /**
   * Funzione opzionale che permete di specificare come renderizzare una singola
   * riga del risultato:
   * ```
   *  renderRow(value, text, index)
   * ```
   * dove:
   * * *value*: il valore della riga da disegnare
   * * *text*: il testo scritto durante la ricerca
   * * *index*: indice della riga da disegnare
   */
  renderRow: PropTypes.func,
  /** Indica se l'autocomplete deve permettere di selezionare solo elementi
     * dal server o anche inserire elementi non presenti nelle risposte ricevute */
  suggest: PropTypes.bool,
  /** Etichetta da visualizzare nel campo che renderizza il componente */
  title: PropTypes.string,
  /** Url da chiamare per lo scaricamento dei dati.
     * Il testo verrà inviato mediante parametro term */
  url: PropTypes.string,
  /** Array delle regole di validazione da applicare al valore dell'autocomplete */
  validations: PropTypes.arrayOf(PropTypes.oneOfType(
    [
      PropTypes.string,
      PropTypes.object,
      PropTypes.func,
    ],
  )),
  /** Il valore iniziale dell'input */
  value: PropTypes.string,
  /**
Metodo alternativo alla prop `url` per caricare gli elementi su cui effettuale la ricerca.

Se `dataSource` è un array, il componente filtrerà la lista in base al testo digitato.

Se `dataSource` è una function, il componente chiamerà la funzione ad ogni ricerca e si aspetta una `Promise` con la lista degli elementi di ritorno.
I parametri della funzione sono `term`, termine di ricerca e `renderProperty`, proprietà di visualizzazione.
   */
  dataSource: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  /**
Proprietà che definisce il comporamento delle ellipsis nel caso di componente in modalità readOnly.
E' possibile definire se presentatorlo top o bottom, left o right oppure se disabilitare la visualizzazione della tooltip.

Vedi componente EllipsisText.
   */
  ellipsis: PropTypes.shape({
    /** mostra la tooltip nella parte alta/bassa */
    top: PropTypes.bool,

    /** mostra la tooltip nella parte sinistra/destra */
    left: PropTypes.bool,

    /** mostra o nasconde la tooltip */
    disabled: PropTypes.bool,
  }),
  /** Converte il testo in upper case */
  upperCase: PropTypes.bool,

  /** Esempio testuale del dato da inserire nel campo */
  placeholder: PropTypes.string,
};

export default AutoComplete;
